import LayoutHR from "layouts/layout-hr";
import React, { useState, useMemo } from "react";
import { Dropdown, Modal, Button, Form, Tab, Tabs } from "react-bootstrap";
import { useCanvasHandler } from "hooks/useCanvasHandler";
import Swal from "sweetalert2";
import {
  TimeOffPoliciesProvider,
  useTimeOffPolicies,
} from "./context/TimeOffPoliciesContext";
import { getDevelopmentBanner } from "utils/devNotice";
import MasterDataFormV2 from "../MasterDataFormV2";
import { useNavigate } from "react-router-dom";
import { deleteRequest } from "services/api";
import { useUser } from "contexts/v1/masterData/UserContext";

function TimeOffPoliciesContent() {
  const {
    policies,
    filterOptions,
    handleSearch,
    refetch,
    assignToUser,
    assignToAll,
    Pagination,
  } = useTimeOffPolicies();
  const { users } = useUser();
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [assignType, setAssignType] = useState("user"); // 'user' or 'all'
  const [assignData, setAssignData] = useState({
    user_id: "",
    period_year: new Date().getFullYear(),
  });

  const { handleError, handleSuccessToast } = useCanvasHandler();

  const handleCreate = () => {
    setShowModal(true);
    setEditingItem(null);
  };

  const handleEdit = (item) => {
    setShowModal(true);
    setEditingItem(item);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await deleteRequest(`/ahr/time-off-policies/${id}`);
        if (response.success) {
          handleSuccessToast(true, "Policy deleted successfully");
          refetch();
        } else {
          handleError("Failed to delete policy");
        }
      } catch (error) {
        handleError(error);
      }
    }
  };

  const handleConvertBoolean = (data) => {
     // Transform boolean fields to 1/0 for API if needed,
     // but MasterDataFormV2 usually handles checkboxes as boolean/1/0 based on config.
     // Here we just pass the data as is, assuming V2 handles it or backend accepts true/false.
     // If backend needs 1/0 for true/false, MasterDataFormV2 might need adjustment or we adjust here.
     // Based on user request "true", backend likely accepts JSON boolean.
     return data;
  }

  const handleFormSuccess = () => {
    setShowModal(false);
    setEditingItem(null);
    refetch();
    handleSuccessToast(true, "Policy saved successfully");
  };

  const handleFormError = (error) => {
    handleError(error);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  // Assignment Logic
  const handleOpenAssign = (policy) => {
    setSelectedPolicy(policy);
    setAssignData({
      user_id: "",
      period_year: new Date().getFullYear(),
    });
    setShowAssignModal(true);
  };

  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    try {
      if (assignType === "user") {
        if (!assignData.user_id) {
          handleError("Please select a user");
          return;
        }
        await assignToUser({
          time_off_policy_id: selectedPolicy.id,
          user_id: assignData.user_id,
          period_year: parseInt(assignData.period_year),
        });
        handleSuccessToast(true, "Policy assigned to user successfully");
      } else {
        await assignToAll({
          time_off_policy_id: selectedPolicy.id,
          period_year: parseInt(assignData.period_year),
        });
        handleSuccessToast(true, "Policy assigned to all users successfully");
      }
      setShowAssignModal(false);
    } catch (error) {
      handleError(error);
    }
  };

  const userOptions = useMemo(() => {
    return (
      users.data?.map((user) => ({
        value: user.id,
        label: `${user.name} (${user.email})`,
      })) || []
    );
  }, [users.data]);

  const formFields = [
    {
      name: "name",
      label: "Name",
      type: "text",
      required: true,
      placeholder: "Policy Name",
    },
    {
      name: "code",
      label: "Code",
      type: "text",
      required: true,
      placeholder: "Policy Code",
    },
    {
      name: "description",
      label: "Description",
      type: "textarea",
      required: false,
    },
    {
      name: "duration",
      label: "Duration (Days)",
      type: "number",
      required: true,
      defaultValue: 0,
    },
    {
      name: "max_request",
      label: "Max Request",
      type: "number",
      required: false,
      defaultValue: 1,
    },
    {
      name: "minus_amount",
      label: "Minus Amount",
      type: "number",
      required: false,
      defaultValue: 0,
    },
    {
      name: "carry_amount",
      label: "Carry Forward Amount",
      type: "number",
      required: false,
      defaultValue: 0,
    },
    {
      name: "carry_expired_months",
      label: "Carry Expired (Months)",
      type: "number",
      required: false,
      defaultValue: 0,
    },
    // Booleans
    {
      name: "include_day_off",
      label: "Include Day Off",
      type: "checkbox",
    },
    {
      name: "allow_half_day",
      label: "Allow Half Day",
      type: "checkbox",
    },
    {
      name: "set_schedule_half_day",
      label: "Set Schedule Half Day",
      type: "checkbox",
    },
    {
      name: "set_default",
      label: "Set Default",
      type: "checkbox",
    },
    {
      name: "emerge_at_join",
      label: "Emerge at Join",
      type: "checkbox",
    },
    {
      name: "show",
      label: "Show",
      type: "checkbox",
      defaultValue: true,
    },
    {
      name: "allow_minus",
      label: "Allow Minus",
      type: "checkbox",
    },
    {
      name: "carry_forward",
      label: "Carry Forward",
      type: "checkbox",
    },
    {
      name: "time_off_compensation",
      label: "Time Off Compensation",
      type: "checkbox",
    },
    {
      name: "attachment_mandatory",
      label: "Attachment Mandatory",
      type: "checkbox",
    },
  ];

  return (
    <LayoutHR>
      {getDevelopmentBanner()}
      <div className="container-xl">
        <div className="page-header d-print-none">
          <div className="row align-items-center">
            <div className="col-auto">
              <button
                className="btn btn-outline-secondary"
                onClick={() => navigate(-1)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="icon icon-tabler icon-tabler-arrow-left"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <line x1="5" y1="12" x2="11" y2="18" />
                  <line x1="5" y1="12" x2="11" y2="6" />
                </svg>
                Back
              </button>
            </div>
            <div className="col">
              <h2 className="page-title">Time Off Policies</h2>
              <div className="text-muted mt-1">
                Manage leave policies and settings
              </div>
            </div>
            <div className="col-auto">
              <button className="btn btn-primary" onClick={handleCreate}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="icon icon-tabler icon-tabler-plus"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add Policy
              </button>
            </div>
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <div className="input-icon">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search policies..."
                    value={filterOptions.search}
                    onChange={() => {}}
                    onKeyDown={handleSearch}
                  />
                  <span className="input-icon-addon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="icon icon-tabler icon-tabler-search"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      strokeWidth="2"
                      stroke="currentColor"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                      <circle cx="10" cy="10" r="7" />
                      <line x1="21" y1="21" x2="15" y2="15" />
                    </svg>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-vcenter table-hover">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Action</th>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Duration</th>
                    <th>Default</th>
                    <th>Show</th>
                  </tr>
                </thead>
                <tbody>
                  {policies.loading ? (
                    <tr>
                      <td colSpan="7" className="text-center">
                        <div
                          className="spinner-border spinner-border-sm"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        Loading policies...
                      </td>
                    </tr>
                  ) : policies.data?.length > 0 ? (
                    policies.data.map((item, index) => (
                      <tr key={item.id}>
                        <td>
                          {(filterOptions.currentPage - 1) * 10 + index + 1}
                        </td>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle
                              variant="default"
                              size="sm"
                              id={`dropdown-${item.id}`}
                            >
                              Actions
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item onClick={() => handleEdit(item)}>
                                Edit
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={() => handleOpenAssign(item)}
                              >
                                Assign
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={() => handleDelete(item.id)}
                                className="text-danger"
                              >
                                Delete
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                        <td>{item.code}</td>
                        <td>{item.name}</td>
                        <td>{item.duration} Days</td>
                        <td>
                          {item.set_default ? (
                            <span className="badge bg-green">Yes</span>
                          ) : (
                            <span className="badge bg-secondary">No</span>
                          )}
                        </td>
                        <td>
                          {item.show ? (
                            <span className="badge bg-green">Yes</span>
                          ) : (
                            <span className="badge bg-secondary">No</span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center">
                        No policies found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {policies.pagination && <Pagination />}
          </div>
        </div>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {editingItem ? "Edit Policy" : "Create Policy"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseModal}
                  ></button>
                </div>
                <div className="modal-body">
                  <MasterDataFormV2
                    apiUrl="/ahr/time-off-policies"
                    initialData={editingItem}
                    fields={formFields}
                    onSuccess={handleFormSuccess}
                    onError={handleFormError}
                    transformData={handleConvertBoolean}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Assign Modal */}
        <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Assign Policy: {selectedPolicy?.name}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Tabs
              activeKey={assignType}
              onSelect={(k) => setAssignType(k)}
              className="mb-3"
            >
              <Tab eventKey="user" title="Assign to User">
                <Form onSubmit={handleAssignSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Select User</Form.Label>
                    <Form.Select
                      value={assignData.user_id}
                      onChange={(e) =>
                        setAssignData({
                          ...assignData,
                          user_id: e.target.value,
                        })
                      }
                      required={assignType === "user"}
                    >
                      <option value="">Select User</option>
                      {userOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Period Year</Form.Label>
                    <Form.Control
                      type="number"
                      value={assignData.period_year}
                      onChange={(e) =>
                        setAssignData({
                          ...assignData,
                          period_year: e.target.value,
                        })
                      }
                      required
                    />
                  </Form.Group>
                  <Button variant="primary" type="submit">
                    Assign to User
                  </Button>
                </Form>
              </Tab>
              <Tab eventKey="all" title="Assign to All">
                <Form onSubmit={handleAssignSubmit}>
                  <div className="alert alert-warning">
                    This will assign the policy <strong>{selectedPolicy?.name}</strong> to <strong>ALL users</strong> for the selected year.
                  </div>
                  <Form.Group className="mb-3">
                    <Form.Label>Period Year</Form.Label>
                    <Form.Control
                      type="number"
                      value={assignData.period_year}
                      onChange={(e) =>
                        setAssignData({
                          ...assignData,
                          period_year: e.target.value,
                        })
                      }
                      required
                    />
                  </Form.Group>
                  <Button variant="danger" type="submit">
                    Assign to All Users
                  </Button>
                </Form>
              </Tab>
            </Tabs>
          </Modal.Body>
        </Modal>
      </div>
    </LayoutHR>
  );
}

export default function TimeOffPoliciesPage() {
  return (
    <TimeOffPoliciesProvider>
      <TimeOffPoliciesContent />
    </TimeOffPoliciesProvider>
  );
}
